import { durationMs } from "odyssey-shared";
import { barMustPage } from "../hud/area-slots.ts";
import type { HudArea, SlotRect } from "../hud/slots.ts";
import { readSetting, writeSetting, type SettingsStore } from "../settings.ts";
import type { ChatLine } from "./store.ts";

/**
 * The minimised chat panel as plain data (P1.9-T30, ADR-068's amendment "the panel minimises"; the design's `ChatMin`
 * and its `min`, `peek` and `mintype` states, a visual reference): the panel folds to a bar on its bottom edge and the
 * world shows through the rest of its slot. Whether it is folded is kept per browser once the viewer chooses; until
 * then it is open, except on an area too narrow for the ten-slot bar, where it starts folded (ADR-061's amendment of
 * 2026-10-06 and its note for P1.9-T133; the author's "Start folded (Recommended)"). The other three states follow from
 * it: the bar becomes an input while the field has focus, and a whisper received peeks above the bar for a time. Pure:
 * the panel holds the state and hands it in.
 */

/**
 * The panel's state: the full panel, the bar, the bar with lines peeking above it, or the bar as an input.
 */
export type ChatMode = "full" | "min" | "peek" | "mintype";

/**
 * The storage key the folded choice is kept under.
 */
export const CHAT_MINIMISED_KEY = "odyssey.chat.minimised";

/**
 * The bar's sizes in logical px, the design's: its height; the padding inside its frame's edge at rest and while
 * it is an input; the open button's and Send's side; the field's height; how many lines peek, a line's height, the
 * gap between two and how far above the bar the lowest ends; and the gaps inside the bar.
 */
export const CHAT_BAR = { height: 14, padding: 1, typingPadding: 0, open: 10, send: 10, field: 12, peekLines: 2, peekLine: 8, peekGap: 1, peekFoot: 3, gap: 2, typingGap: 1 } as const;

/**
 * How long a line peeks above the bar: a chat bubble's time from the motion table.
 */
export const PEEK_MS = durationMs("bubble-min");

/**
 * The state the panel is in.
 *
 * @param state Whether the panel is `minimised`, whether the field has focus (`typing`), and whether a line is
 *   `peeking`.
 * @returns Full while it is not minimised; else the input while typing, the peek while a line peeks, and the bar.
 * @example
 * chatMode({ minimised: true, typing: true, peeking: true });
 * // "mintype"
 */
export function chatMode(state: { readonly minimised: boolean; readonly typing: boolean; readonly peeking: boolean }): ChatMode {
  if (!state.minimised) {
    return "full";
  }
  return state.typing ? "mintype" : state.peeking ? "peek" : "min";
}

/**
 * What had focus as the panel folded or opened: the minimise control, the resize grip, the bar's open control, the
 * field, or anything else.
 */
export type ChatFocus = "minimise" | "grip" | "open" | "input" | "other";

/**
 * Where focus goes once the panel has folded or opened, so the keyboard never loses it: the control that had focus
 * leaves the page with its row, and the control that takes its place takes the focus: the open button for the
 * minimise control and for the resize grip, which both leave with the tab row, and the minimise control for the
 * open button. Focus anywhere else stays, the field's while typing included.
 *
 * @param from What had focus.
 * @param minimised Whether the panel is minimised now.
 * @returns The control to focus, or null to leave focus where it is.
 */
export function focusAfterToggle(from: ChatFocus, minimised: boolean): "minimise" | "open" | null {
  if ((from === "minimise" || from === "grip") && minimised) {
    return "open";
  }
  return from === "open" && !minimised ? "minimise" : null;
}

/**
 * The bar's rectangle: the panel's width on the panel's bottom edge.
 *
 * @param panel The full panel's rectangle, whole logical pixels.
 * @returns The bar's.
 * @example
 * barRect({ left: 4, top: 234, width: 198, height: 122 });
 * // { left: 4, top: 342, width: 198, height: 14 }
 */
export function barRect(panel: SlotRect): SlotRect {
  return { left: panel.left, top: panel.top + panel.height - CHAT_BAR.height, width: panel.width, height: CHAT_BAR.height };
}

/**
 * Reads a kept value as the folded choice.
 *
 * @param raw The kept text, or null when nothing is kept.
 * @returns True for exactly "1", false for exactly "0", and null for nothing or anything else: no choice made.
 */
export function parseMinimised(raw: string | null): boolean | null {
  return raw === "1" ? true : raw === "0" ? false : null;
}

/**
 * Reads the kept choice.
 *
 * @param store Where it is kept, or null when there is none.
 * @returns Whether the viewer minimised the panel or opened it; null when nothing usable is kept or the store
 *   cannot be read, where the area decides (`startsMinimised`).
 */
export function readMinimised(store: SettingsStore | null): boolean | null {
  return readSetting(CHAT_MINIMISED_KEY, parseMinimised, store);
}

/**
 * Whether the panel is minimised on an area before the viewer has chosen: where the ability bar must page
 * (`barMustPage`, every area under 632 logical pixels wide), so a small window shows the world and the bar, on a touch
 * page, where every panel starts closed (ADR-061's amendment of 2026-10-31, decision 9), and open everywhere else, Ability bar: Five at a time or not (the author's "Start folded (Recommended)", 2026-10-08).
 *
 * @param area The area, whole logical pixels.
 * @returns True where the panel starts as its bar.
 * @example
 * startsMinimised({ width: 480, height: 500 });
 * // true
 */
export function startsMinimised(area: HudArea): boolean {
  return area.touch === true || barMustPage(area);
}

/**
 * Whether the panel is minimised: the viewer's kept choice, or what the area starts with while none is kept.
 *
 * @param kept The kept choice, or null when the viewer has made none.
 * @param area The area, whole logical pixels.
 * @returns True for a minimised panel.
 */
export function minimisedOn(kept: boolean | null, area: HudArea): boolean {
  return kept ?? startsMinimised(area);
}

/**
 * Keeps the choice.
 *
 * @param minimised Whether the panel is minimised.
 * @param store Where to keep it, or null when there is none.
 * @returns True when the store took it.
 */
export function writeMinimised(minimised: boolean, store: SettingsStore | null): boolean {
  return writeSetting(CHAT_MINIMISED_KEY, minimised ? "1" : "0", store);
}

/**
 * Whether a line peeks above the bar: a whisper received, and the page's refusal or failure of what was typed, which
 * would otherwise be answered in a log nobody sees, and a Party line from another member (ADR-101 decision 25). Every
 * other line only counts on the bar.
 *
 * @param line The line.
 * @param self The player's names, whose own Party line does not peek, or null before the welcome.
 * @returns True for a line that peeks.
 */
export function peeks(line: ChatLine, self: { readonly firstName: string; readonly familyName: string } | null = null): boolean {
  if (line.kind === "party") {
    return self === null || line.firstName.toLowerCase() !== self.firstName.toLowerCase() || line.familyName.toLowerCase() !== self.familyName.toLowerCase();
  }
  return line.kind === "error" || (line.kind === "whisper" && !line.sent);
}

/**
 * A line peeking: its id in the log, and when its time ends on the page's clock.
 */
export interface PeekEntry {
  readonly id: number;
  readonly until: number;
}

/**
 * The lines peeking, and the newest line id already weighed, so no line is weighed twice.
 */
export interface PeekState {
  readonly entries: readonly PeekEntry[];
  readonly through: number;
}

/**
 * Weighs the lines that arrived since the last look: each that peeks starts its time now, and at most the newest two
 * are kept.
 *
 * @param peek The lines peeking and the newest id weighed.
 * @param lines The log.
 * @param now The page's clock.
 * @param self The player's names, or null before the welcome.
 * @returns The new record, or `peek` itself when no line is new.
 */
export function takePeeks(peek: PeekState, lines: readonly ChatLine[], now: number, self: { readonly firstName: string; readonly familyName: string } | null = null): PeekState {
  const newest = lines.at(-1)?.id;
  if (newest === undefined || newest <= peek.through) {
    return peek;
  }
  const fresh = lines.filter((line) => line.id > peek.through && peeks(line, self)).map((line) => ({ id: line.id, until: now + PEEK_MS }));
  return { entries: [...peek.entries, ...fresh].slice(-CHAT_BAR.peekLines), through: newest };
}

/**
 * The entries whose time is not over.
 *
 * @param entries The lines peeking.
 * @param now The page's clock.
 * @returns Those still peeking, or `entries` itself when none has ended.
 */
export function livePeeks(entries: readonly PeekEntry[], now: number): readonly PeekEntry[] {
  return entries.every((entry) => entry.until > now) ? entries : entries.filter((entry) => entry.until > now);
}

/**
 * When the next peeking line's time ends.
 *
 * @param entries The lines peeking.
 * @returns The earliest end, or null when none peeks.
 */
export function nextPeekEnd(entries: readonly PeekEntry[]): number | null {
  return entries.length === 0 ? null : Math.min(...entries.map((entry) => entry.until));
}

/**
 * The log's lines that peek, oldest first; a line the log no longer holds (after `/clear`) is passed over.
 *
 * @param entries The lines peeking.
 * @param lines The log.
 * @returns The lines to draw above the bar.
 */
export function peekLines(entries: readonly PeekEntry[], lines: readonly ChatLine[]): readonly ChatLine[] {
  return entries.flatMap((entry) => lines.find((line) => line.id === entry.id) ?? []);
}
